"use client";

import { JSX, KeyboardEvent, useId, useMemo, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { AreaChart, Area } from "@/components/charts/area-chart";
import { Grid } from "@/components/charts/grid";
import { XAxis } from "@/components/charts/x-axis";
import { ChartTooltip } from "@/components/charts/tooltip/chart-tooltip";
import { ChartLegendHoverProvider } from "@/components/charts/chart-legend-hover";
import { useLegendClickSync } from "@/components/charts/use-legend-click-sync";
import {
  Legend,
  LegendItem,
  LegendLabel,
  LegendMarker,
  LegendValue,
} from "@/components/charts/legend";
import { emitModuleInteraction } from "@/lib/module-interaction";
import { stripInlineMarkdown } from "@/utils/inline-markdown";
import {
  METRIC_TABS_MAX_METRICS,
  type ModuleMetricTab,
  type ModuleMetricTabsChartData,
} from "@/types/modules";
import { chartPaletteStyle, importsTokenScope } from "./imports-token-scope";
import { useViewerLanguage } from "@/lib/i18n";
import { formatModuleValue } from "./shared/value-format";
import { CHART_PLOT_HEIGHT } from "@/config/layout";

const CHART_HEIGHT = CHART_PLOT_HEIGHT.metricTabs;
const CURRENT_COLOR = "var(--chart-1)";
const COMPARE_COLOR = "var(--chart-3)";

function rollup(values: number[], mode: ModuleMetricTab["aggregate"] = "sum"): number {
  const v = values.filter(Number.isFinite);
  if (!v.length) return 0;
  if (mode === "last") return v[v.length - 1];
  const sum = v.reduce((a, b) => a + b, 0);
  return mode === "average" ? sum / v.length : sum;
}

export default function ModuleMetricTabsChart({
  data,
  grayscale,
  accent,
  className,
}: {
  data: ModuleMetricTabsChartData;
  grayscale?: boolean;
  accent?: number;
  className?: string;
}): JSX.Element {
  const { t, intlLocale } = useViewerLanguage();
  const periods = useMemo(
    () => (data?.periods ?? []).map((p) => stripInlineMarkdown(p)),
    [data?.periods],
  );
  const metrics = useMemo(
    () => (data?.metrics ?? []).slice(0, METRIC_TABS_MAX_METRICS),
    [data?.metrics],
  );
  const compareLabel = stripInlineMarkdown(data?.compareLabel) || t("Comparison");

  const [selected, setSelected] = useState(0);
  const activeIndex = Math.min(selected, Math.max(0, metrics.length - 1));
  const active = metrics[activeIndex];
  const tabsId = useId();
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const rowRef = useRef<HTMLDivElement | null>(null);

  const [legendHovered, setLegendHovered] = useState<number | null>(null);
  const {
    activeIndex: legendActive,
    handleSelect,
    setLabelRef,
    selectedOrigin,
    legendSelectedIndex,
    elementIndex,
    toggleElementIndex,
    setActiveIndex,
  } = useLegendClickSync();

  const fmt = (m: ModuleMetricTab | undefined, v: number) =>
    formatModuleValue(v, {
      format: m?.format,
      digits: m?.digits ?? (m?.format === "percent" ? 1 : 0),
      unit: m?.unit,
      locale: intlLocale,
    });

  const hasCompare = !!active?.compareValues && active.compareValues.length > 0;

  const chartData = useMemo(
    () =>
      periods.map((x, i) => ({
        x,
        current: active?.values[i] ?? null,
        compare: hasCompare ? (active?.compareValues?.[i] ?? null) : null,
      })),
    [periods, active, hasCompare],
  );

  const seriesLabel =
    stripInlineMarkdown(data?.seriesLabel) || stripInlineMarkdown(active?.label) || t("Current");

  const legendItems = [
    {
      id: "current",
      label: seriesLabel,
      color: CURRENT_COLOR,
      value: fmt(active, rollup(active?.values ?? [], active?.aggregate)),
    },
    ...(hasCompare
      ? [
          {
            id: "compare",
            label: compareLabel,
            color: COMPARE_COLOR,
            dashed: true,
            value: fmt(active, rollup(active?.compareValues ?? [], active?.aggregate)),
          },
        ]
      : []),
  ];

  const revealTab = (index: number) => {
    const row = rowRef.current;
    const tab = tabRefs.current[index];
    if (!row || !tab) return;
    const start = tab.offsetLeft;
    const end = start + tab.offsetWidth;
    let left = row.scrollLeft;
    if (start < left) left = start;
    else if (end > left + row.clientWidth) left = end - row.clientWidth;
    if (left === row.scrollLeft) return;
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    row.scrollTo({ left, behavior: reduce ? "auto" : "smooth" });
  };

  const changeTab = (to: number, element: Element | null) => {
    revealTab(to);
    if (to === activeIndex) return;
    emitModuleInteraction(element, "tab_change", { kind: "tab", index: to }, {
      from: activeIndex,
      to,
    });
    setSelected(to);
    setLegendHovered(null);
    setActiveIndex(null);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
    e.preventDefault();
    const next =
      (activeIndex + (e.key === "ArrowRight" ? 1 : -1) + metrics.length) % metrics.length;
    const el = tabRefs.current[next];
    changeTab(next, el);
    el?.focus({ preventScroll: true });
  };

  return (
    <div className={cn("flex w-full min-w-0 flex-col gap-4", className)} style={importsTokenScope(grayscale, accent)}>
      <div
        ref={rowRef}
        role="tablist"
        aria-label={t("Metrics")}
        onKeyDown={onKeyDown}
        className="relative flex w-full min-w-0 snap-x flex-row flex-nowrap overflow-x-auto overscroll-x-contain rounded-xl border border-[var(--border)] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {metrics.map((m, i) => {
          const on = i === activeIndex;
          const value = rollup(m.values, m.aggregate);
          const last = i === metrics.length - 1;
          return (
            <button
              key={m.id ?? i}
              ref={(el) => {
                tabRefs.current[i] = el;
              }}
              id={`${tabsId}-tab-${i}`}
              type="button"
              role="tab"
              aria-selected={on}
              aria-controls={`${tabsId}-panel`}
              tabIndex={on ? 0 : -1}
              onClick={(e) => changeTab(i, e.currentTarget)}
              className={cn(
                "flex min-w-[8rem] flex-[1_0_auto] snap-start flex-col gap-1 px-3 py-2.5 text-left outline-none transition-colors",
                !last && "border-r border-[var(--border)]",
                on
                  ? "bg-[var(--track)]"
                  : "hover:bg-[var(--track)]/50 focus-visible:bg-[var(--track)]/50",
              )}
            >
              <span
                className={cn(
                  "max-w-[12rem] truncate text-xs",
                  on ? "text-[var(--fg)]" : "text-[var(--muted)]",
                )}
              >
                {stripInlineMarkdown(m.label)}
              </span>
              <span className="max-w-[12rem] truncate text-base font-medium tracking-tight tabular-nums text-[var(--fg)]">
                {fmt(m, value)}
              </span>
            </button>
          );
        })}
      </div>

      <ChartLegendHoverProvider
        hoveredIndex={legendHovered}
        onHoverChange={setLegendHovered}
        selectedIndex={legendActive}
        selectedOrigin={selectedOrigin}
        elementIndex={elementIndex}
        onElementToggle={toggleElementIndex}
      >
        <div
          id={`${tabsId}-panel`}
          role="tabpanel"
          aria-labelledby={`${tabsId}-tab-${activeIndex}`}
          className="flex w-full flex-col gap-3"
          style={chartPaletteStyle(grayscale, accent)}
        >
          <div style={{ height: CHART_HEIGHT }}>
            <AreaChart
              key={activeIndex}
              data={chartData as unknown as Record<string, unknown>[]}
              xDataKey="x"
              xScaleKind="band"
              margin={{ top: 12, right: 12, bottom: 28, left: 12 }}
              aspectRatio="auto"
              className="h-full"
            >
              <Grid horizontal vertical={false} strokeDasharray="3,5" />
              <Area
                dataKey="current"
                stroke={CURRENT_COLOR}
                strokeWidth={2}
                fill={CURRENT_COLOR}
                fillOpacity={0.16}
                gradientToOpacity={0}
                fadeEdges
              />
              {hasCompare && (
                <Area
                  dataKey="compare"
                  stroke={COMPARE_COLOR}
                  strokeWidth={1.5}
                  fill={COMPARE_COLOR}
                  fillOpacity={0}
                  gradientToOpacity={0}
                  dashFromIndex={0}
                  dashArray="4,4"
                  fadeEdges
                />
              )}
              <XAxis />
              <ChartTooltip
                showDatePill
                showCrosshair
                showDots
                rows={(point) => [
                  {
                    color: CURRENT_COLOR,
                    label: seriesLabel,
                    value: fmt(active, Number(point.current ?? 0)),
                  },
                  ...(hasCompare
                    ? [
                        {
                          color: COMPARE_COLOR,
                          label: compareLabel,
                          value: fmt(active, Number(point.compare ?? 0)),
                        },
                      ]
                    : []),
                ]}
              />
            </AreaChart>
          </div>

          <div className="border-t border-dashed border-[var(--border-dashed-soft)] pt-3">
            <Legend
              items={legendItems}
              hoveredIndex={legendHovered}
              onHoverChange={setLegendHovered}
              selectedIndex={legendSelectedIndex}
              onSelect={handleSelect}
              onLabelRef={setLabelRef}
              className="flex flex-row flex-wrap gap-1"
            >
              <LegendItem className="flex min-w-0 items-center gap-2 px-2 py-1">
                <LegendMarker variant="arrow" />
                <LegendLabel />
                <LegendValue />
              </LegendItem>
            </Legend>
          </div>
        </div>
      </ChartLegendHoverProvider>
    </div>
  );
}
